import {ServiceBase} from "../ServiceBase";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {IOmgService} from "./omg.service.interface";
import {OmgMessageCollection, OmgMessageModel} from "./omg-message-collection";
import {ExternalLinksZIndexes} from "../external-links/external-links-z-indexes";
import {GNWP_SOURCES} from "../../../sdk/src/components/external_links/external_links_consts";
import {IOMGParsedData} from "../../../sdk/src/services/player_messages_service/firebase/handlers/messages_handler";
import {Check} from "@sparkware/app-utils";
import {IOpenOmgOptions} from "../external-links/external-links.service.interface";

interface IShowMessagesOptions {
    markAsDisplayed: boolean;
}

export class OmgService extends ServiceBase implements IOmgService {

    constructor(services: IServiceFactoryExtended) {
        super(services);

        this._messageCollection = new OmgMessageCollection(services);
        this._routesToBeIgnored = this._parseRoutesToBeIgnored();


        this.reaction(() => this.services.user.isAuthorized,
            this._onAuthorizationStateChangedHandler,
            {
                fireImmediately: true
            });

        this.services.sdk.events.onFirebaseOmgReceived(this._onFirebaseOmgReceivedHandler)

        this.services.sdk.events.onExternalLinkClosed(this._onExternalLinkClosedHandler);

        this.services.sdk.events.onDepositWidgetClosed(this._onDepositWidgetClosedHandler)
    }

    private readonly _routesToBeIgnored: string[] = [];

    private _parseRoutesToBeIgnored(): string[] {
        const ignorePopupURLs = this.services.configuration.servicesConfigs.Omg.ignorePopopURLs;
        if(!ignorePopupURLs) {
            return [];
        }

        try {
            const result = JSON.parse(ignorePopupURLs);
            if(Check.isArray(result)) {
                return result.map(path => path.toString().toLowerCase())
            } else {
                this.services.logger.error(`Omg.ignorePopopURLs configuration is not an array of strings`);
            }

        } catch (err) {
            this.services.logger.error(`Failed to parse Omg.ignorePopopURLs configuration`, err);
            return [];
        }
    }

    private _shouldIgnoreOmgForCurrentRoute(): boolean {
        const currentPathName = this.services.location.currentUri.pathName.toString().toLowerCase();
        for(let route of this._routesToBeIgnored) {
            if(currentPathName.startsWith(route)) {
                return true;
            }
        }

        return false;
    }


    private _onAuthorizationStateChangedHandler = async (isAuthorized: boolean): Promise<void> => {
        if(isAuthorized) {
            await this._readOmgMessagesFromServer();
            await this._showMessages(this._messageCollection.getMessagesToDisplay(), {
                markAsDisplayed: true
            });
        } else {
            this._messageCollection.clear();
        }
    }

    private _onExternalLinkClosedHandler = async (source: string) => {
        if (GNWP_SOURCES.CASHIER === source || GNWP_SOURCES.MY_ACCOUNT === source || GNWP_SOURCES.OMG === source) {
            await this._readOmgMessagesFromServer();
        }
    }

    private _onDepositWidgetClosedHandler = async () => {
        await this._readOmgMessagesFromServer();
    }

    private _onFirebaseOmgReceivedHandler = async (omgData: IOMGParsedData) => {
        let zIndex: ExternalLinksZIndexes = ExternalLinksZIndexes.Omg;
        if(this.services.gameRunner.currentRunningGame && omgData.OpenedWithinGame === '0') {
            zIndex = ExternalLinksZIndexes.OmgBehindTheGame;
        }
        await this._openOMG({
            messages: omgData.Messages,
            openedWithinGame: omgData.OpenedWithinGame,
            zIndex: zIndex
        });
    }

    get numberOfAwaitingMessages() {
        return this._messageCollection.getMessagesToDisplay().length;
    }

    private readonly _messageCollection: OmgMessageCollection;


    public async openOmgAwaitingMessages(): Promise<void> {
        await this._showMessages(this._messageCollection.getMessagesToDisplay(), {
            markAsDisplayed: true
        });
    }

    private async _readOmgMessagesFromServer() {
        const response = await this.services.api.oMGController.getAwaitingMessages();

        if(response.MessagesJSON) {
            this._messageCollection.push(response.MessagesJSON);
        } else {
            this._messageCollection.clear();
        }
    }

    private _convertMessagesToXml(messagesToDisplay: OmgMessageModel[]): string {
        return `<Messages>${messagesToDisplay.map(msg => msg.toXml()).join()}</Messages>`
    }

    private async _showMessages(messagesToDisplay: OmgMessageModel[], options: IShowMessagesOptions): Promise<void> {
        if(messagesToDisplay.length === 0) {
            return;
        }

        if(options.markAsDisplayed) {
            this._messageCollection.markMessagesAsDisplayed(messagesToDisplay);
        }

        await this._openOMG({
            messages: this._convertMessagesToXml(messagesToDisplay),
            openedWithinGame: '0',
            zIndex: ExternalLinksZIndexes.OmgBehindTheGame
        });
    }

    private async _openOMG(options: IOpenOmgOptions): Promise<void> {
        if(!this._shouldIgnoreOmgForCurrentRoute()) {
            await this.services.externalLinks.openOMG(options);
        }
    }
}
